import { Avatar, EmptyState, Kbd, Modal, Spinner } from '@nexo/ui';
import { Search } from 'lucide-react';
import {
  createContext,
  useContext,
  useDeferredValue,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import { useTeam } from '../../layouts/TeamContext';
import { formatDay, formatTime } from '../../lib/format';
import { useSearch } from '../../lib/queries';
import { channelLabel } from '../channels/ChannelSidebar';
import { StatusIcon } from '../tasks/TaskIcons';
import { Trans, useTranslation } from 'react-i18next';

const SearchContext = createContext<(() => void) | null>(null);

/** Provides the team search dialog and the Ctrl/⌘K shortcut. */
export function SearchProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <SearchContext.Provider value={() => setOpen(true)}>
      {children}
      <SearchDialog open={open} onClose={() => setOpen(false)} />
    </SearchContext.Provider>
  );
}

export function useSearchDialog(): () => void {
  const open = useContext(SearchContext);
  if (!open) throw new Error('useSearchDialog must be used inside <SearchProvider>');
  return open;
}

function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { team, channels, openTask } = useTeam();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const { t } = useTranslation();
  const deferred = useDeferredValue(q.trim());
  const results = useSearch(team.id, deferred);

  const close = () => {
    setQ('');
    onClose();
  };

  const hasResults =
    results.data && (results.data.messages.length > 0 || results.data.tasks.length > 0);

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('Buscar en {{team}}', { team: team.name })}
      size="lg"
    >
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-subtle" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('Mensajes y tareas…')}
          aria-label={t('Buscar')}
          className="h-11 w-full rounded-xl bg-surface-2 pr-10 pl-10 text-[15px] ring-1 ring-inset ring-border placeholder:text-subtle focus:ring-2 focus:ring-accent focus:outline-none"
        />
        {results.isFetching && (
          <Spinner className="absolute top-1/2 right-3.5 -translate-y-1/2 text-muted" />
        )}
      </div>

      {deferred.length < 2 ? (
        <p className="flex items-center justify-center gap-2 py-8 text-sm text-subtle">
          <Trans
            i18nKey="Escribe al menos 2 caracteres · <kbd>Esc</kbd> para cerrar"
            components={{ kbd: <Kbd>Esc</Kbd> }}
          />
        </p>
      ) : results.data && !hasResults ? (
        <EmptyState
          title={t('Sin resultados')}
          description={t('No hay nada que coincida con «{{query}}».', { query: deferred })}
        />
      ) : (
        results.data && (
          <div className="flex flex-col gap-5">
            {results.data.tasks.length > 0 && (
              <section>
                <h3 className="mb-1 px-2 text-[11px] font-semibold tracking-wider text-subtle uppercase">
                  {t('Tareas')}
                </h3>
                {results.data.tasks.map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => {
                      close();
                      openTask({ mode: 'edit', taskId: task.id });
                    }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm hover:bg-surface-2"
                  >
                    <StatusIcon status={task.status} />
                    <span className="truncate">{task.title}</span>
                  </button>
                ))}
              </section>
            )}
            {results.data.messages.length > 0 && (
              <section>
                <h3 className="mb-1 px-2 text-[11px] font-semibold tracking-wider text-subtle uppercase">
                  {t('Mensajes')}
                </h3>
                {results.data.messages.map((message) => {
                  const channel = channels.find((c) => c.id === message.channelId);
                  return (
                    <button
                      key={message.id}
                      type="button"
                      onClick={() => {
                        close();
                        void navigate(`/app/t/${team.id}/c/${message.channelId}`);
                      }}
                      className="flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left hover:bg-surface-2"
                    >
                      <Avatar
                        id={message.author.id}
                        name={message.author.displayName}
                        src={message.author.avatarUrl}
                        size="sm"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-2 text-xs text-muted">
                          <strong className="font-semibold text-text">
                            {message.author.displayName}
                          </strong>
                          {channel &&
                            (channel.kind === 'dm' ? channelLabel(channel) : `#${channel.name}`)}
                          <span className="text-subtle">
                            {formatDay(message.createdAt)} · {formatTime(message.createdAt)}
                          </span>
                        </span>
                        <span className="line-clamp-2 text-sm">{message.body}</span>
                      </span>
                    </button>
                  );
                })}
              </section>
            )}
          </div>
        )
      )}
    </Modal>
  );
}
